import { colours } from "@shared/ui.design-system";
import { PlusIcon, TickIcon } from "@shared/ui.icons";
import type { KeyboardEvent } from "react";
import React, { useEffect, useRef, useState } from "react";

import { Typography } from "../Typography/Typography";

import {
  checkbox,
  checkboxContainer,
  checkboxIcons,
  container,
  labelText
} from "./Checkbox.css";

export type CheckboxProps = {
  dangerouslySetInnerHTML?: boolean;
  disabled?: boolean;
  hideCheckBoxIconOnHover?: boolean;
  id: string;
  initialChecked?: boolean;
  label?: string;
  labelPosition?: "left" | "right";
  onChange: (event: boolean) => void;
  subLabel?: string;
  variant?: "primary" | "secondary";
};

const getTickColour = (checked: boolean, disabled: boolean): string => {
  if (checked && disabled) return colours.secondary.deep;
  if (checked) return colours.secondary.light;
  return "none";
};

const renderLabel = (
  id: string,
  label: string | undefined,
  subLabel: string | undefined,
  dangerouslySetInnerHTML: boolean
) => (
  <div className={labelText}>
    {dangerouslySetInnerHTML ? (
      <Typography
        id={`${id}-label`}
        dangerouslySetInnerHTML={{ __html: label ?? "" }}
        variant="body-md"
      />
    ) : (
      <Typography id={`${id}-label`}>{label}</Typography>
    )}
    {subLabel && (
      <Typography
        id={`${id}-info`}
        colour="primary-medium"
        variant="smallprint-md"
      >
        {subLabel}
      </Typography>
    )}
  </div>
);

const renderTickIcon = (isChecked: boolean, isDisabled: boolean) => (
  <TickIcon
    className={checkboxIcons.check}
    fill={getTickColour(isChecked, isDisabled)}
    stroke={getTickColour(isChecked, isDisabled)}
  />
);

const renderPlusIcon = (
  isChecked: boolean,
  isDisabled: boolean,
  hideOnHover: boolean
) => {
  const shouldShow = !isChecked && !isDisabled && !hideOnHover;
  return (
    <PlusIcon
      className={
        shouldShow
          ? `${checkboxIcons.add} ${checkboxIcons.showAddOnHover}`
          : checkboxIcons.hideAdd
      }
      fill="black"
    />
  );
};

/**
 * A stylised and accessible checkbox component with label, sublabel, and icon transitions.
 * - Shows a tick icon when checked.
 * - Shows a plus icon on hover when unchecked (if enabled).
 * - Supports left/right label positioning, disabled state, and custom visuals via variants.
 *
 * @param {CheckboxProps} props - The props for the Checkbox component.
 * @param {boolean} [props.dangerouslySetInnerHTML=false] - If true, injects the `label` using `dangerouslySetInnerHTML`.
 * @param {boolean} [props.disabled=false] - Disables the checkbox and prevents interaction.
 * @param {boolean} [props.hideCheckBoxIconOnHover=false] - Hides the hover plus icon when true.
 * @param {string} props.id - Unique identifier for input and labels (used for accessibility).
 * @param {boolean} [props.initialChecked=false] - Initial checked state for the checkbox.
 * @param {string} [props.label] - Label text or HTML content associated with the checkbox.
 * @param {string} [props.subLabel] - Optional sublabel rendered beneath the main label.
 * @param {"left" | "right"} [props.labelPosition="right"] - Position of the label relative to the checkbox.
 * @param {(checked: boolean) => void} props.onChange - Callback fired when the checked state changes.
 * @param {"primary" | "secondary"} [props.variant="primary"] - Visual style variant (affects border and fill colour).
 * @returns {JSX.Element} The rendered checkbox component.
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  dangerouslySetInnerHTML = false,
  disabled = false,
  hideCheckBoxIconOnHover = false,
  id,
  initialChecked = false,
  label,
  labelPosition = "right",
  onChange,
  subLabel,
  variant = "primary"
}) => {
  const [isChecked, setIsChecked] = useState(initialChecked);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIsChecked(initialChecked);
  }, [initialChecked]);

  const handleChange = () => {
    if (disabled) return;
    const next = !isChecked;
    setIsChecked(next);
    onChange(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleChange();
    }
  };

  const checkboxContent = (
    <div className={checkboxContainer}>
      <input
        id={id}
        ref={inputRef}
        type="checkbox"
        className={checkbox({ variant })}
        checked={isChecked}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        disabled={disabled}
        aria-disabled={disabled}
        aria-checked={isChecked}
        aria-labelledby={`${id}-label`}
        aria-label={label}
      />
      {renderTickIcon(isChecked, disabled)}
      {renderPlusIcon(isChecked, disabled, hideCheckBoxIconOnHover)}
    </div>
  );

  return (
    <label className={container[labelPosition]} htmlFor={id}>
      {labelPosition === "left" &&
        renderLabel(id, label, subLabel, dangerouslySetInnerHTML)}
      {checkboxContent}
      {labelPosition === "right" &&
        renderLabel(id, label, subLabel, dangerouslySetInnerHTML)}
    </label>
  );
};
